<template>
  <div>
    <Filters :filtersValues :preset :resetFilters :submitFilters />
    <RevCard class="overflow-hidden">
      <NoResults
        v-if="status === 'success' && data && data.results.length === 0"
      />

      <template v-if="status === 'success' && data && data.results.length > 0">
        <div class="flex flex-col p-16 md:flex-row md:items-center">
          <ResultsCounter :count="data.count" :preset />
          <PageSizeSelect
            v-model="filtersValues.pageSize"
            @update:model-value="submitFilters"
          />
        </div>
        <div
          class="m-16 mb-32 flex flex-col space-y-8 md:flex-row md:space-x-8 md:space-y-0"
        >
          <ConfirmSelectedOrders
            :preset
            :selectedOrders
            @success="handleBulkActionSuccess"
          />
          <CreateBackshipLabels
            :preset
            :selectedOrders
            @success="handleBulkActionSuccess"
          />
          <PrintPackingSlips
            :preset
            :selectedOrders
            @success="handleBulkActionSuccess"
          />
        </div>

        <Table
          v-model:selectedIds="selectedIds"
          :orders="data.results"
          :refreshOrders="submitFilters"
        />
        <Pagination
          class="py-56"
          :current-page="filtersValues.page"
          :page-count
          @click="({ page }) => setCurrentPage(page)"
        />
      </template>

      <RevLoadingScreen
        v-if="status === 'pending'"
        class="py-72"
        :text="i18n(translations.loading)"
        variant="light"
      />
    </RevCard>
  </div>
</template>

<script setup lang="ts">
import { useFilters, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import { getMerchantOrders } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { openModal } from '@ds/components/ModalBase'

import translations from './Tab.translations'
import type { Preset } from './Tab.types'
import { sanitizedPreset } from './Tab.utils'
import ConfirmSelectedOrders from './components/BulkActions/ConfirmSelectedOrders/ConfirmSelectedOrders.vue'
import CreateBackshipLabels from './components/BulkActions/CreateBackshipLabels/CreateBackshipLabels.vue'
import PrintPackingSlips from './components/BulkActions/PrintPackingSlips/PrintPackingSlips.vue'
import { INITIAL_FILTERS_VALUES } from './components/Filters/Filters.constants'
import Filters from './components/Filters/Filters.vue'
import NoResults from './components/NoResults/NoResults.vue'
import PageSizeSelect from './components/PageSizeSelect/PageSizeSelect.vue'
import ResultsCounter from './components/ResultsCounter/ResultsCounter.vue'
import Table from './components/Table/Table.vue'
import {
  FINANCIAL_DETAILS,
  MODAL_NAME,
  ORDER_DETAILS,
} from './components/Table/components/ActionCell/components/MoreActions/components/OrderRecapDrawer/OrderRecapDrawer.constants'
import { useOrderRecapDrawer } from './components/Table/composables/useOrderRecapDrawer'

const route = useRoute()
const i18n = useI18n()

const { orderId } = useOrderRecapDrawer()

const {
  filtersValues,
  sanitizedFiltersValues,
  submitFilters,
  resetFilters,
  setCurrentPage,
} = useFilters({
  ...INITIAL_FILTERS_VALUES,
  endDate: new Date().toISOString().split('T')[0],
  orderId: orderId.value || INITIAL_FILTERS_VALUES.orderId,
})

// preset is the last bit of the route.path that identify the page and is sent as queryParams to the API
const preset = computed(() => route.path.split('/').reverse()[0] as Preset)

const queryParams = computed(() => ({
  ...sanitizedFiltersValues.value,
  ...sanitizedPreset(preset.value),
}))

const { data, status } = useHttpFetch(getMerchantOrders, {
  queryParams,
  watch: [queryParams],
})

const pageCount = computed(() =>
  data.value
    ? Math.ceil(data.value.count / parseInt(filtersValues.value.pageSize, 10))
    : 1,
)

const selectedIds = ref<string[]>([])
const selectedOrders = computed(() =>
  (data.value?.results || []).filter((order) =>
    selectedIds.value.includes(order.id.toString()),
  ),
)

async function handleBulkActionSuccess() {
  selectedIds.value = []
  await submitFilters()
}

watch(
  () => route.hash,
  (newHash, oldHash) => {
    const [target, id] = route.hash.split('=')
    const tab = target.replace('#', '')
    if (
      newHash !== oldHash &&
      [ORDER_DETAILS, FINANCIAL_DETAILS].includes(tab) &&
      id
    ) {
      openModal(MODAL_NAME + id)
    }
  },
  { immediate: true },
)
</script>
